/**
 * autoReplyShadow — de código del backend a español que se entiende.
 *
 * Módulo puro: sin React, sin red, sin estado. Solo diccionarios y cuatro
 * funciones que los consultan.
 *
 * LA REGLA QUE IMPORTA: un código que no está aquí **no se pinta**. Es la
 * misma que ya aplica `crmIntelligence.reasonText`, y existe porque el
 * backend puede emitir motivos que este fichero todavía no conoce —el
 * camino de error guarda el nombre de la clase de la excepción, por
 * ejemplo—. Enseñar `ZeroDivisionError` en la ficha de un lead no informa
 * a nadie; enseñar una invención en su lugar informa mal, que es peor.
 *
 * Los motivos compuestos llevan prefijo (`late:human_replied`,
 * `provider_rejected:503`, `residual_vocabulary:7`), así que se parte por
 * ":" antes de buscar.
 */

/** Los desenlaces posibles de una ráfaga. Vocabulario cerrado del backend. */
export const OUTCOME_META = {
  would_send: {
    label: "HABRÍA RESPONDIDO",
    hint: "Pasó todas las puertas. Con el envío encendido, esto habría salido solo.",
    cls: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300",
  },
  human_required: {
    label: "LO MANDA UNA PERSONA",
    hint: "Hay borrador, pero algo exige que lo lea alguien antes.",
    cls: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  },
  blocked: {
    label: "NO DEBE RESPONDER",
    hint: "No corresponde ninguna respuesta automática a este lead ahora.",
    cls: "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300",
  },
  no_analysis: {
    label: "SIN ANÁLISIS",
    hint: "No había nada que redactar.",
    cls: "bg-stone-50 border-stone-200 text-stone-600 dark:bg-stone-900 dark:border-stone-700 dark:text-stone-300",
  },
  error: {
    label: "ERROR",
    hint: "Falló algo nuestro al procesar la ráfaga.",
    cls: "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300",
  },
  sent: {
    label: "ENVIADO",
    hint: "Salió de verdad.",
    cls: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300",
  },
  send_blocked: {
    label: "ENVÍO DETENIDO",
    hint: "Era candidata y algo la paró antes de llegar al proveedor.",
    cls: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  },
  send_uncertain: {
    label: "ENVÍO INCIERTO",
    hint: "Puede haber llegado al teléfono. Lo tiene que mirar una persona.",
    cls: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  },
};

/**
 * Los tres niveles de autonomía, de menos a más libertad.
 *
 * El backend los emite en MAYÚSCULAS (`SHADOW_ELIGIBLE`) y aquí las claves
 * están en minúsculas: `levelLabel` normaliza. Antes no lo hacía, y el chip
 * del nivel no se pintaba nunca con datos reales — los fixtures del test
 * usaban minúsculas y no lo veían.
 */
export const LEVEL_LABELS = {
  blocked: "Bloqueado",
  human_required: "Requiere persona",
  shadow_eligible: "Candidato a autónomo",
};

/**
 * Los motivos. El backend los emite desde tres sitios —la política, la
 * puerta previa al análisis y literales del propio worker— así que esta
 * lista no puede darse por cerrada, y por eso lo que no está no se pinta.
 */
export const REASON_LABELS = {
  // Lo que 3B/3C ya marcaba
  commercial_negotiation: "Hay negociación comercial",
  financial_claim: "Afirma algo financiero",
  legal_claim: "Afirma algo legal",
  sensitive_commitment: "Adquiere un compromiso delicado",
  missing_information: "Falta información para contestar",
  unsupported_project_claim: "Afirma algo del proyecto sin respaldo",
  opted_out: "El lead pidió que no le escribamos",
  no_draft: "No hay borrador",
  low_confidence: "El modelo no estaba seguro",
  human_managing_conversation: "Una persona del equipo está llevando un trámite con este lead",
  // Puertas de la política
  consent_opted_out: "El lead se dio de baja",
  // HISTÓRICOS. El consentimiento previo dejó de bloquear automatizaciones,
  // así que ninguna decisión NUEVA puede llevar estos motivos. Se siguen
  // traduciendo porque hay decisiones de septiembre que los tienen escritos,
  // y el texto dice que la regla ya no está: una pantalla que enseña el
  // motivo de entonces sin decir que caducó invita a seguir creyéndolo.
  consent_missing: "No constaba consentimiento de WhatsApp (regla retirada)",
  no_whatsapp_consent: "No constaba consentimiento de WhatsApp (regla retirada)",
  lead_archived: "El lead está archivado",
  lead_converted: "El lead ya es cliente",
  no_valid_phone: "No hay un teléfono utilizable",
  artifact_version_mismatch: "El borrador se generó con otras reglas",
  decision_no_reply: "El análisis decidió no responder",
  decision_human_review: "El análisis pidió revisión humana",
  model_requires_review: "El modelo pidió revisión humana",
  lead_stage_terminal: "La etapa del lead ya está cerrada",
  lead_stage_sensitive: "La etapa del lead es delicada",
  draft_missing: "No hay borrador que enviar",
  draft_too_long: "El borrador no cabe en un WhatsApp",
  context_stale: "El contexto había cambiado",
  fingerprint_mismatch: "El análisis ya no corresponde a esta conversación",
  fact_no_longer_usable: "Un hecho citado dejó de ser utilizable",
  knowledge_changed: "La base de conocimiento cambió",
  claim_ref_coverage_unverified: "No se puede demostrar que todo lo que dice esté respaldado",
  schedule_unverified: "Propone una cita que nadie ha confirmado",
  conversation_moved_on: "La conversación ya siguió por otro lado",
  human_replied_after_inbound: "Una persona ya había contestado",
  outside_free_form_window: "Fuera de la ventana de 24 h",
  template_required: "Haría falta una plantilla, no texto libre",
  unknown_context: "Faltaba un dato para poder decidir",
  unknown_review_reason: "El modelo dio un motivo que no reconocemos",
  policy_error: "Falló la política al evaluar",
  // Puertas previas al análisis y fronteras del envío
  ai_not_configured: "La IA no está configurada",
  cost_cap_lead: "Tope de coste de este lead alcanzado",
  cost_cap_global: "Tope de coste global alcanzado",
  cost_accounting_unavailable: "No se pudo contar el gasto",
  send_not_allowlisted: "El envío no llega a este lead (solo prueba)",
  send_not_allowlisted_late: "El envío dejó de llegar a este lead mientras se procesaba",
  lead_missing: "El lead ya no existe",
  human_replied: "Una persona ya había contestado",
  lead_replied: "El lead volvió a escribir",
  human_acked: "Alguien marcó la conversación como atendida",
  window_closed: "La ventana de 24 h estaba cerrada",
  unclassified_outbound_before_send: "Salió algo de esta conversación que no supimos leer",
  send_disabled: "El envío está apagado",
  send_disabled_late: "El envío se apagó mientras se procesaba",
  send_cap_lead: "Tope diario de respuestas de este lead",
  send_cap_global: "Tope diario global de respuestas",
  send_too_soon: "Demasiado pronto desde la anterior",
  consecutive_cap: "Demasiadas respuestas seguidas sin que hable una persona",
  stale_inbound: "El mensaje ya no era reciente",
  coverage_lost: "La cobertura dejó de poder demostrarse",
  snapshot_changed: "El análisis cambió antes de enviar",
  policy_changed: "La política dejó de autorizarlo",
  claim_lost: "Otro proceso se llevó el trabajo",
  empty_draft: "El borrador estaba vacío",
  residual_number: "Queda una cifra que nadie ha comprobado",
  residual_vocabulary: "Quedan palabras sin clasificar",
  residual_script: "El texto usa un alfabeto que no sabemos leer",
  rate_accounting_unavailable: "No se pudieron contar los envíos",
  sending_lease_lost: "El proceso murió durante el envío",
  transport_uncertain: "No se sabe si llegó",
  // Acciones y promesas (3B)
  action_invalid: "Pidió una acción que no se puede ejecutar",
  unplanned_action_promise: "Promete algo que el sistema no va a hacer",
  // El documento: por qué no se podía o no salió
  action_unknown: "Pidió una acción que no existe",
  asset_missing: "No hay ningún dossier activo para ese idioma",
  asset_inactive: "El dossier no está activo",
  asset_kind_unknown: "Ese documento no existe en el catálogo",
  asset_mime_invalid: "El fichero no es un PDF",
  asset_too_large: "El fichero pasa del límite de WhatsApp",
  asset_object_missing: "El fichero del dossier no está en el servidor",
  asset_size_mismatch: "El fichero no tiene el tamaño registrado",
  asset_hash_mismatch: "El fichero no es el que se registró (hash distinto)",
  asset_link_unavailable: "No se pudo generar el enlace de descarga",
  asset_check_failed: "No se pudo comprobar el dossier",
  asset_already_sent: "El dossier ya se le había enviado",
  asset_repair_pending: "Una persona tiene pendiente mandarlo o comprobarlo",
  // El reenvío de un documento que el lead ya recibió y pide otra vez.
  asset_not_delivered: "No consta ninguna entrega anterior que reenviar",
  asset_delivery_unresolved: "Un envío anterior del documento sigue sin resolver",
  redelivery_request_unverified: "No consta un mensaje del lead que lo pida otra vez",
  redelivery_request_served: "Esa petición ya se había atendido",
  redelivery_too_soon: "Se acababa de enviar y WhatsApp aún no lo confirmaba",
  asset_unavailable: "El dossier no se podía entregar",
  asset_changed: "Se activó otra versión del dossier entre medias",
  asset_too_late: "El documento llegaba demasiado tarde tras el texto",
  asset_plan_missing: "No constaba qué documento enviar",
  asset_park_lost: "Otro proceso tomó el trabajo del documento",
  asset_lease_lost: "El proceso se detuvo con el documento en vuelo",
  asset_worker_error: "Falló algo nuestro al enviar el documento",
  text_uncertain: "No se sabe si el texto llegó",
  text_not_sent: "El texto no salió",
  send_in_progress: "Había otro envío a medias para este lead",
  // La respuesta a nuestra pregunta y la autorización del primer documento.
  ambiguous_answer: "Contestó «sí» o «no» a una pregunta de opciones: no dice cuál",
  offer_declined: "Dijo que no a nuestra oferta",
  answered_prompt: "Contestó a nuestra pregunta",
  no_request_evidence: "Nada del turno autorizaba mandar el documento",
  resend_target_unknown: "Pide otra vez un documento que no sabemos si es el dossier",
  first_send_unauthorized: "El envío del documento no decía qué lo autorizaba",
  first_send_authorization_lost: "Lo que autorizaba el documento ya no se sostiene",
  double_opener: "El mensaje tenía dos entradas seguidas",
  action_without_announcement: "El documento saldría sin que el texto lo dijera",
};

/**
 * Las ACCIONES: lo que el bot habría hecho además de contestar.
 *
 * Hoy solo existe una —mandar un activo comercial, el dossier— y se pinta
 * como la pediste: «Enviar dossier ES», con el fichero, la versión y el
 * tamaño que se fijaron AL DECIDIR. No el activo de hoy: una decisión de la
 * semana pasada enseña la versión que se juzgó entonces.
 */
export const ASSET_KIND_LABELS = { dossier: "dossier" };

const ACTION_VERBS = { send_sales_asset: "Enviar", resend_sales_asset: "Reenviar" };

export function actionLabel(action) {
  const verbo = action ? ACTION_VERBS[action.type] : null;
  if (!verbo) return null;
  const kind = ASSET_KIND_LABELS[action.asset_kind];
  if (!kind) return null;
  const idioma = action.language ? String(action.language).toUpperCase() : "";
  return `${verbo} ${kind} ${idioma}`.trim();
}

/** «~24 MB». Aproximado a propósito: el hash exacto va aparte. */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1_000_000) return `~${Math.round(n / 1_000_000)} MB`;
  if (n >= 1_000) return `~${Math.round(n / 1_000)} KB`;
  return `${n} B`;
}

/** Qué pasó con el documento. Lo que no está aquí no se pinta. */
export const ASSET_SEND_META = {
  text_sent: { label: "El texto salió; el documento espera", tone: "neutral" },
  asset_sending: { label: "El documento va de camino", tone: "neutral" },
  completed: { label: "Documento enviado", tone: "ok" },
  accepted_pending: {
    label: "Aceptado por WhatsApp, pendiente de confirmar", tone: "neutral",
  },
  asset_failed: { label: "El documento no salió", tone: "warn" },
  asset_uncertain: { label: "El documento pudo llegar o no", tone: "warn" },
  asset_blocked: { label: "El documento se detuvo antes de enviarse", tone: "warn" },
  asset_skipped: { label: "El documento no se intentó", tone: "neutral" },
};

export function assetSendMeta(state) {
  return ASSET_SEND_META[state] || null;
}

/**
 * Lo que dice HOY el libro de envíos del documento, que manda sobre el
 * estado que el worker escribió al decidir. Ese estado se congela en
 * `accepted_pending` o `completed`; el webhook de WhatsApp sigue llegando
 * después, y sin mirarlo la tarjeta diría «pendiente de confirmar» de un
 * PDF que ya se leyó, o «enviado» de uno que Meta rechazó.
 */
export const ASSET_DELIVERY_META = {
  failed: { label: "WhatsApp no pudo entregar el documento", tone: "warn" },
  sent: { label: "Documento enviado", tone: "ok" },
  delivered: { label: "Documento entregado", tone: "ok" },
  read: { label: "Documento entregado y leído", tone: "ok" },
};

// Estados en los que el documento llegó (o pudo llegar) al proveedor, y
// que por tanto el libro puede resolver hacia arriba.
const ASSET_SEND_LIVE_STATES = new Set([
  "accepted_pending", "asset_sending", "asset_uncertain", "completed",
]);

export function assetSendView(envio) {
  if (!envio) return null;
  // Un fallo del libro gana siempre: es terminal y es lo último que se sabe.
  if (envio.delivery_status === "failed" || envio.delivery === "failed") {
    return ASSET_DELIVERY_META.failed;
  }
  const base = assetSendMeta(envio.state);
  if (!base || !ASSET_SEND_LIVE_STATES.has(envio.state)) return base;
  if (envio.delivery_status === "read") return ASSET_DELIVERY_META.read;
  if (envio.delivery_status === "delivered") return ASSET_DELIVERY_META.delivered;
  if (envio.delivery === "sent") return ASSET_DELIVERY_META.sent;
  return base;
}

/** Por qué la ACCIÓN estuvo mal. Separado del texto: pueden fallar por separado. */
export const ACTION_FEEDBACK_CATEGORIES = [
  { value: "should_not_send", label: "No tocaba mandar el dossier" },
  { value: "should_have_sent", label: "Tocaba mandarlo y no lo pidió" },
  { value: "wrong_asset", label: "Otro documento" },
  { value: "wrong_language", label: "Otro idioma" },
  { value: "too_early", label: "Todavía no" },
  { value: "other", label: "Otro" },
];

export function actionCategoryLabel(value) {
  const found = ACTION_FEEDBACK_CATEGORIES.find((c) => c.value === value);
  return found ? found.label : null;
}

/** Por qué una respuesta estuvo mal. Vocabulario cerrado, para poder contar. */
export const FEEDBACK_CATEGORIES = [
  { value: "tone", label: "Tono" },
  { value: "wrong_fact", label: "Dato incorrecto" },
  { value: "too_long", label: "Demasiado largo" },
  { value: "too_pushy", label: "Demasiado agresivo" },
  { value: "did_not_answer", label: "No contestó a lo que preguntaban" },
  { value: "other", label: "Otro" },
];

/**
 * Por qué no hay borrador. UNO POR CAUSA, y la diferencia importa.
 *
 * Antes solo había dos y se elegían deduciendo de que el texto fuera
 * nulo, así que una decisión de hace diez minutos bloqueada antes de
 * llamar al modelo se anunciaba como «anterior a que se guardara». Falso,
 * y de los que hacen desconfiar de todo lo demás que dice la pantalla.
 *
 * Ahora el motivo lo escribe el backend en el momento y aquí solo se
 * traduce. `legacy_not_recorded` es la ausencia del campo — y solo ésa
 * significa que la fila es antigua de verdad.
 */
const DRAFT_UNAVAILABLE = {
  legacy_not_recorded:
    "Borrador no recuperable — esta decisión es anterior a que se guardara.",
  blocked_before_analysis:
    "Sin borrador — la respuesta se bloqueó antes de generar el análisis.",
  analysis_failed:
    "Sin borrador — el análisis falló y no llegó a producir texto.",
  no_analysis: "Sin borrador — no había nada que responder.",
  empty_draft: "Sin borrador — el análisis no produjo texto.",
};

export function outcomeMeta(code) {
  return OUTCOME_META[code] || null;
}

export function levelLabel(code) {
  if (!code) return null;
  return LEVEL_LABELS[String(code).toLowerCase()] || null;
}

/**
 * El motivo en español, o `null` si no lo conocemos.
 *
 * `null` y no el código crudo: la ficha de un lead no es un log.
 */
export function reasonText(code) {
  if (!code) return null;
  const texto = String(code);
  // Los compuestos van en los DOS sentidos, porque los dos existen:
  // en `residual_vocabulary:7` el motivo es lo de antes de los dos
  // puntos, y en `late:human_replied` es lo de después — «late» solo
  // dice cuál de las dos fronteras lo paró.
  const [antes, despues] = [texto.split(":")[0], texto.split(":").slice(1).join(":")];
  return REASON_LABELS[texto] || REASON_LABELS[antes]
    || (despues ? REASON_LABELS[despues] : null) || null;
}

/** Los motivos que sí sabemos decir, sin repetir. */
export function reasonList(reasons) {
  const vistos = new Set();
  const out = [];
  (reasons || []).forEach((r) => {
    const texto = reasonText(r);
    if (texto && !vistos.has(texto)) {
      vistos.add(texto);
      out.push(texto);
    }
  });
  return out;
}

export function draftUnavailableText(reason) {
  // Un motivo que no conocemos NO se anuncia como antigüedad: decir «esta
  // decisión es anterior» sobre algo que no lo es fue el fallo original.
  return DRAFT_UNAVAILABLE[reason] || "Sin borrador.";
}

export function categoryLabel(value) {
  const found = FEEDBACK_CATEGORIES.find((c) => c.value === value);
  return found ? found.label : null;
}

/** Porcentaje entero sobre el total, o null si no hay nada que dividir. */
export function pct(part, total) {
  if (!total) return null;
  return Math.round((Number(part || 0) / total) * 100);
}
